import { createAsyncThunk } from '@reduxjs/toolkit';
import ArchivalService from 'services/archival.service';
import { statusCodes } from 'core/constants/report';
import {
  IArchivalRecordQuery, ICreateArchivalExport, ICreateArchivalRequest,
} from 'core/models/archival';
import { ArchivalActionTypes } from './archival-store.state';

/**
 * Turns a rejected archival call into something worth showing on the form.
 *
 * Two shapes have to be handled: api.ts's response interceptor rejects 422 and 403
 * with `error.response` itself, while everything else arrives as the full axios
 * error with the response nested under `.response`.
 *
 * 422 is only ever the in-flight guard (one archival request per namespace at a
 * time), so it gets phrased for a user instead of passing through the API's
 * "namespace 'leads': ..." wording. 400s name the actual problem (unresolvable
 * window, empty or duplicated namespace) and are shown verbatim.
 */
const getApiErrorMessage = (error: any, fallback: string): string => {
  const response = error?.response ?? error;
  const data = response?.data;

  if (response?.status === statusCodes.Unprocessable) {
    return 'This namespace already has an archival request in progress. Wait for it to finish before raising another one.';
  }

  const message = data?.Message ?? data?.message ?? data?.Error ?? data?.error;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

export const createArchivalRequest = createAsyncThunk(
  ArchivalActionTypes.CREATE_ARCHIVAL_REQUEST,
  async (payload: ICreateArchivalRequest, { rejectWithValue }) => {
    try {
      return await new ArchivalService().createArchivalRequest(payload);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Could not raise the archival request. Please try again.'));
    }
  },
);

export const getArchivalRequests = createAsyncThunk(
  ArchivalActionTypes.GET_ARCHIVAL_REQUESTS,
  async () => new ArchivalService().getArchivalRequests(),
);

export const createArchivalExport = createAsyncThunk(
  ArchivalActionTypes.TRIGGER_EXPORT_REQUEST,
  async (payload: ICreateArchivalExport, { rejectWithValue }) => {
    try {
      return await new ArchivalService().createArchivalExport(payload);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Could not queue the export. Please try again.'));
    }
  },
);

export const getArchivalExports = createAsyncThunk(
  ArchivalActionTypes.GET_EXPORT_LIST_REQUEST,
  async () => new ArchivalService().getArchivalExports(),
);

/**
 * Rejection value that means "the lookup route is not deployed", as opposed to
 * "the archive has no such record". Both are 404s.
 */
export const RECORD_LOOKUP_UNAVAILABLE = 'RECORD_LOOKUP_UNAVAILABLE';

/**
 * Echo answers an unregistered route with its own generic body -
 * `{"Message":"code=404, message=Not Found"}` - while the handler's own
 * not-found carries a real reason. get-record is not built yet, so today every
 * call takes the first branch; telling them apart keeps the screen from claiming
 * the archive was searched when it never was.
 */
const isRouteMissing = (message: unknown) => typeof message === 'string'
  && /code=404/i.test(message)
  && /message=Not Found/i.test(message);

/**
 * A genuine 404 resolves as "no record" rather than rejecting - it is the API's
 * documented answer for an id that is not in the archive. Anything else is a real
 * failure.
 */
export const getArchivedRecord = createAsyncThunk(
  ArchivalActionTypes.GET_RECORD_REQUEST,
  async (query: IArchivalRecordQuery, { rejectWithValue }) => {
    try {
      return await new ArchivalService().getArchivedRecord(query);
    } catch (error) {
      const response = (error as any)?.response ?? error;
      if (response?.status === statusCodes.NotFound) {
        const data = response?.data;
        if (isRouteMissing(data?.Message ?? data?.message)) {
          return rejectWithValue(RECORD_LOOKUP_UNAVAILABLE);
        }
        return null;
      }
      return rejectWithValue(error);
    }
  },
);

// There is no polling and no status endpoint: the two list calls above return
// current status, and the Refresh button in the page header re-runs them.
